<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Biometria | Provedores" :on-add="() => $router.push({ name: 'create-biometric-providers' })" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.credentials`]="{ item }">
            <v-chip size="small" color="info">
              {{ Object.keys(item.credentials || {}).length }} credenciais
            </v-chip>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>

import { ref, Ref, useTemplateRef } from 'vue'
import TPageTitle from '@/components/TPageTitle.vue'
import type { DataTableFilter, DataTableHeader } from '@/types/DataTable'
import BiometricProviderService from '@/services/BiometricProviderService'
import type { BiometricProvider } from '@/types/BiometricProvider'
import { Resources } from '@/plugins/permissions/typing'
import { useTextFilter } from '@/helpers/reports/textFilter'
import DataTable from '@/components/datatable/DataTable.vue'
import { useRouter } from 'vue-router'
import { useDialog } from '@/stores/dialog'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import { useAbility } from '@casl/vue'

const { can } = useAbility()
const router = useRouter()
const loader = useLoader()
const dialog = useDialog()
const snackbar = useSnackBar()

const resource: Resources = 'biometric-providers'

const datatable = useTemplateRef('datatable')

const headers: Ref<DataTableHeader[]> = ref([
  { key: 'id', value: 'id', title: 'ID', sortable: false },
  { key: 'name', value: 'name', title: 'Nome', sortable: false },
  { key: 'classpath', value: 'classpath', title: 'Classe', sortable: false },
  { key: 'credentials', value: 'credentials', title: 'Credenciais', sortable: false, align: 'center' },
  { key: 'actions', value: '', title: 'Ações', sortable: false, align: 'center' },
])

const actions = ref({
  read: {
    callback: (item: BiometricProvider) => router.push({ name: 'show-biometric-providers', params: { id: item.id } }),
    icon: 'fa-solid fa-magnifying-glass',
    color: 'primary',
  },
  update: {
    callback: (item: BiometricProvider) => router.push({ name: 'edit-biometric-providers', params: { id: item.id } }),
    condition: () => can('update', 'biometric-providers'),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: (item: BiometricProvider) => destroy(item),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
})

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({
    field: 'name',
    label: 'Nome',
    icon: 'fa-regular fa-signature',
    required: false,
  }),
])

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    return await (new BiometricProviderService()).index(page, filters, per_page)
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar os provedores biométricos',
      'error',
    )

    throw error
  }
}

async function destroy(item: BiometricProvider) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir o provedor biométrico? Essa ação é irreversível',
  })

  if (!confirm) return

  try {
    loader.increment()
    await (new BiometricProviderService()).destroy(item.id)

    snackbar.show(
      'Provedor biométrico excluído com sucesso.',
      'success',
    )

    if (datatable.value !== null) {
      datatable.value.fetch(1)
    }
  } finally {
    loader.decrement()
  }
}
</script>
